import React, {useMemo, useRef, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import ProjectGroup from "../../models/ProjectGroup";
import {useAuth} from "../../providers/AuthProvider";
import NavBar from "../../components/nav/NavBar";
import LoadingBanner from "../../components/LoadingBanner";
import ErrorBanner from "../../components/ErrorBanner";
import {Outlet, useParams} from "react-router-dom";
import Modal from "../../components/Modal";
import EditProjectGroupModal from "./EditProjectGroupModal";
import DeleteProjectGroupModal from "./DeleteProjectGroupModal";
import {useAPI} from "../../config/API";


const ProjectGroupsLayout = (props) => {

    const {auth} = useAuth();
    const params = useParams();
    const [isDisabled, setIsDisabled] = useState(false);
    const [errorFeedback, setErrorFeedback] = useState(undefined);
    const [editingGroup, setEditingGroup] = useState(null);
    const createGroupRef = useRef(null)
    const deleteGroupRef = useRef(null)
    const queryClient = useQueryClient()
    const {
        data:groupData,
        isFetching,
        isInitialLoading:isLoading,
        error,
        refetch
    } = useAPI(['fetchClientProjectGroups', auth.clientOrg?.()?.id],  {
            onError: async (data) => {
                // console.log('onSuccess', data);
                setErrorFeedback({
                    title: 'Something went wrong',
                    message: 'Failed to load project groups',
                })
            },
            enabled: !!auth && !!auth.clientOrg?.()?.id
        }
    )

    const {
        data:users,
        isFetching: isFetchingUsers,
        isInitialLoading,
        refetch: refetchUsers
    } = useAPI(['fetchClientUsers', auth.clientOrg?.()?.id],  {
            enabled: !!auth && !!auth.clientOrg?.()?.id && !!groupData
        }
    )

    const groups = useMemo(() => {
        return groupData?.groups || undefined;
    }, [groupData?.groups, isFetching, isFetchingUsers]);

    const currentGroup = useMemo(() => {
        if(!groups?.length || !params?.groupId) return;

        return TB.find(groups, ['id', parseInt(params?.groupId)])
    }, [groups, params]);


    /*
    |--------------------------------------------------------------------------
    | Create/Edit Group Form Modal
    |--------------------------------------------------------------------------
    */
    const onEditGroup = async(group) => {
        let title = "Create Custom Group"
        if (group) {
            title = "Edit Custom Group"
        }

        group = group || {};
        await setEditingGroup(group);
        createGroupRef.current.showModal({title});
    }

    const onDeleteGroup = async(group) => {

        await setEditingGroup(group);
        deleteGroupRef.current.showModal({title: 'Delete group?'});
    }

    /*
    |--------------------------------------------------------------------------
    | Post Requests - Delete Group, Update Group, Create Group
    |--------------------------------------------------------------------------
    */

    const deleteGroup = async(group, props) => {
        // TODO post delete
        if (isDisabled) {
            return;
        }

        await setIsDisabled(true)
        await setErrorFeedback(null)

        let {
            new_group_id
        } = props;

        let response = await editingGroup.delete({new_group_id});

        if (!response) {
            await setIsDisabled(false)

            return TB.showServerErrorToast();

            // return setErrorFeedback({
            //     ...TB.parseRequestError(ProjectGroup.DeleteGroupRequest),
            //     onRetry: () => deleteGroup(group, props)
            // })
        }


        await deleteGroupRef.current.hideModal();
        await queryClient.invalidateQueries(['fetchClientProjectGroups', auth.clientOrg().id])

        await setIsDisabled(false)
        return TB.toast(`Group Deleted Successfully`)
    }

    const createOrUpdateGroup = async(group) => {
        if (isDisabled) {
            return;
        }

        await setIsDisabled(true)
        await setErrorFeedback(null)

        if (!group.id) {
            let createdGroup = await ProjectGroup.create(group);

            if (!createdGroup) {
                await setIsDisabled(false)

                //todo show error toast;
                return TB.showServerErrorToast();
                // return setErrorFeedback({
                //     ...TB.parseRequestError(ProjectGroup.CreateGroupRequest),
                //     onRetry: () => createOrUpdateGroup(group)
                // })
            }

            await createGroupRef.current.hideModal();

        } else {

            let updatedGroup = await group.update();

            if (!updatedGroup) {

                await setIsDisabled(false)
                return TB.showServerErrorToast();
                // return setErrorFeedback({
                //     ...TB.parseRequestError(ProjectGroup.UpdateGroupRequest),
                //     onRetry: () => createOrUpdateGroup(group)
                // })
            }

            await createGroupRef.current.hideModal();

        }
        await queryClient.invalidateQueries(['fetchClientProjectGroups', auth.clientOrg().id])
        await setIsDisabled(false)

        return TB.toast(`Group ${group.id ? 'Updated' : 'Created'} Successfully`)
    }

    const disabled = useMemo(() => isFetching || isFetchingUsers || isDisabled, [isFetching, isFetchingUsers, isDisabled]);

    const publicGroup = useMemo(() => {
        if(!groupData || !users?.length || isFetchingUsers || isFetching) return;


        let publicGroup = groupData.publicGroup;

        if (publicGroup) {
            return ProjectGroup.make({
                ...publicGroup,
                members: users,
                member_count: users.length,
            })
        }
    }, [groupData, users,isFetching, isFetchingUsers]);
    return (
        <div className='container-fluid'>

            <NavBar
                back={currentGroup && {path: '/client/org/groups'}}
                title={currentGroup ? currentGroup.name : "Groups"}
                stats={[
                    currentGroup ? {
                        title: 'Members',
                        value: currentGroup?.members?.length || '-'
                    } : {
                        title: 'Total',
                        value: groups?.length || '-'
                    }
                ]}
                button={(currentGroup || auth?.canManageGroups()) && (
                    (
                        <button
                            onClick={() => onEditGroup(currentGroup)}
                            className="btn btn-primary px-4 py-2 ml-auto">
                            {currentGroup ? 'Add/Remove Members' : 'Create Custom Group'}
                        </button>
                    )
                )}
            />

            {(isFetching || isFetchingUsers) && <LoadingBanner/>}

            {TB.isObject(errorFeedback) && (
                <ErrorBanner {...errorFeedback}
                             onRetry={async () => {
                                 let {
                                     onRetry
                                 } = {...errorFeedback};

                                 if(!onRetry) {
                                     onRetry = refetch;
                                 }

                                 await setErrorFeedback(null);
                                 return onRetry()
                             }}/>
            )}


            {!isFetching && !isFetchingUsers && groupData && !TB.isObject(errorFeedback) && (
                <Outlet context={{
                    groups: groupData?.groups,
                    publicGroup,
                    disabled,
                    onEditGroup,
                    createOrUpdateGroup,
                    onDeleteGroup
                }}/>
            )}

            <Modal ref={createGroupRef}
                   modalClass="edit-group-modal"
                   allowScrollPagination={true}
                   onCloseAction={() => setEditingGroup(null)}>
                {users && editingGroup && (
                    <EditProjectGroupModal
                        onSubmit={group => createOrUpdateGroup(group)}
                        disabled={disabled}
                        group={editingGroup}
                        projects={groupData?.projects || []}
                        users={users}
                    />
                )}
            </Modal>
            <Modal ref={deleteGroupRef}
                   modalClass="edit-group-modal"
                   allowScrollPagination={true}
                   onCloseAction={() => setEditingGroup(null)}>
                {groups && editingGroup && (
                    <DeleteProjectGroupModal
                        onDelete={deleteGroup}
                        disabled={disabled}
                        onRequestClose={() => deleteGroupRef.current.hideModal()}
                        group={editingGroup}
                        groups={groups}
                    />
                )}

            </Modal>
        </div>
    );
};

export default ProjectGroupsLayout;